import { TYPOGRAPHY, DEVICE } from '../../assets/styles';
import React, { FC, useState } from 'react';
import styled, { CSSProperties } from 'styled-components';
import { Language, LanguageOption } from '../../types';
import { ArrowIcon } from '../../assets/svg';
import { CSSProp } from 'styled-components/macro';

interface Props<T> {
  onSwitchLanguage: (item: LanguageOption<T>) => void;
  currentLanguage: LanguageOption<T>;
  languagesOptions: LanguageOption<T>[];
  className?: string;
  rootCSS?: CSSProp;
}

export function LanguageDropdownLayout<T extends string = Language>({
  currentLanguage,
  languagesOptions,
  onSwitchLanguage,
  className,
  rootCSS,
}: Props<T>) {
  const [isOpened, setIsOpened] = useState(false);

  const parseLanguageName = (str: string) => {
    const splittedStr = str.split('_');

    if (splittedStr[1]) {
      if (isNaN(Number(splittedStr[1]))) {
        return splittedStr[1];
      }
    }

    return splittedStr[0];
  };

  return (
    <Root $CSS={rootCSS} className={className} onClick={() => setIsOpened(!isOpened)}>
      <IconWrap>{currentLanguage.icon}</IconWrap>
      <LangCode className="langCode">{parseLanguageName(currentLanguage.name) || ''}</LangCode>
      <ArrowIconWrap className="arrowIcon" $isOpened={isOpened}>
        {<ArrowIcon />}
      </ArrowIconWrap>
      <DropdownContainer $isOpened={isOpened}>
        {languagesOptions.map(item => (
          <ListItem key={item.name} onClick={() => onSwitchLanguage(item)}>
            <IconWrap>{item.icon}</IconWrap>
            <LangCode>{parseLanguageName(item.name) || ''}</LangCode>
          </ListItem>
        ))}
      </DropdownContainer>
    </Root>
  );
}

const Root = styled.div<{ $CSS?: CSSProp }>`
  position: relative;
  right: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  ${({ $CSS }) => $CSS}
`;

const IconWrap = styled.div`
  margin-right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LangCode = styled.div`
  ${TYPOGRAPHY.bodyRegular14}
  color: var(--color6);
  text-transform: uppercase;
  margin-right: 4px;
  font-weight: 500;
  letter-spacing: 0.04em;
`;

const ArrowIconWrap = styled.div<{ $isOpened: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  ${({ $isOpened }) => $isOpened && 'transform: rotate(180deg);'}

  & > svg {
    width: 14px;
    height: 14px;
  }

  @media ${DEVICE.laptop} {
    & > svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const DropdownContainer = styled.div<{ $isOpened: boolean }>`
  ${({ $isOpened }) => ($isOpened ? 'display: block;' : 'display: none;')}
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  border: 1px solid var(--color10);
  border-radius: 10px;
  background-color: var(--color5);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  min-width: 100%;
  z-index: 100;
  animation: langDropdownFadeIn 0.15s ease-out;

  @keyframes langDropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ListItem = styled.button`
  display: flex;
  padding: 8px 12px;
  align-items: center;
  width: 100%;
  border-radius: 6px;
  margin: 0 4px;
  width: calc(100% - 8px);
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--color8);
    cursor: pointer;
  }
`;
